import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import AppsIcon from '@mui/icons-material/Apps';
import CheckIcon from '@mui/icons-material/Check';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HubIcon from '@mui/icons-material/Hub';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShareIcon from '@mui/icons-material/Share';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CampaignIcon from '@mui/icons-material/Campaign';
import type { SvgIconComponent } from '@mui/icons-material';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import type { ErpModule } from '@/api/platform.api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { switchWorkspace } from '@/store/authSlice';
import { usePermissions } from '@/hooks/usePermissions';
import { getLauncherModules } from '@/utils/moduleAccess';
import { rememberPreSwitchPath, workspaceModuleHome } from '@/utils/moduleNavigation';
import { isHeadModuleSubscribed } from '@/hooks/useLauncherModuleSync';
import { notify } from '@/core/toast/notify';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';

const MODULE_ICONS: Partial<Record<ErpModule, SvgIconComponent>> = {
  POS: PointOfSaleIcon,
  CRM: HubIcon,
  HR: GroupsIcon,
  FINANCE: AccountBalanceIcon,
  PROJECTS: AssignmentIcon,
  DESK: SupportAgentIcon,
  SOCIAL: ShareIcon,
  PROCUREMENT: RequestQuoteIcon,
  WAREHOUSE: LocalShippingIcon,
  LEAD_GEN: CampaignIcon,
  MARKETING: CampaignIcon,
};

type PanelCoords = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
};

interface AppLauncherProps {
  activeModule: ErpModule;
  variant?: 'default' | 'sidebar';
  collapsed?: boolean;
}

export function AppLauncher({
  activeModule,
  variant = 'default',
  collapsed = false,
}: AppLauncherProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const switching = useAppSelector((s) => s.auth.loading);
  const { has } = usePermissions();

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelCoords, setPanelCoords] = useState<PanelCoords | null>(null);

  const subscribedApps = useMemo(
    () => getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const canSwitchApps = subscribedApps.length >= 2;
  const activeEntry = getModuleEntryByErpModule(activeModule);
  const ActiveIcon = MODULE_ICONS[activeModule] ?? HubIcon;
  const activeLabel = activeEntry?.label ?? activeModule;

  useLayoutEffect(() => {
    if (!open) {
      setPanelCoords(null);
      return;
    }
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      if (variant === 'sidebar') {
        setPanelCoords({
          bottom: window.innerHeight - rect.top + 8,
          left: rect.left,
          width: Math.max(rect.width, collapsed ? 15 * 16 : rect.width),
        });
      } else {
        setPanelCoords({
          top: rect.bottom + 8,
          left: rect.left,
          width: Math.min(22 * 16, window.innerWidth - 32),
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, variant, collapsed]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-app-launcher-panel="true"]')) return;
      setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const handleSwitch = async (module: ErpModule) => {
    if (module === activeModule) {
      setOpen(false);
      return;
    }
    if (!isHeadModuleSubscribed(module, enabledModules, accessibleModules)) {
      notify.error(`Module ${module} is not enabled for this organization`);
      setOpen(false);
      return;
    }
    if (!subscribedApps.some((entry) => entry.erpModule === module)) {
      notify.error('You do not have access to this application');
      setOpen(false);
      return;
    }
    rememberPreSwitchPath(location.pathname, location.search);
    setOpen(false);
    try {
      await dispatch(switchWorkspace(module)).unwrap();
      const home = workspaceModuleHome(module);
      navigate(home, { replace: true });
      notify.success(`Switched to ${getModuleEntryByErpModule(module)?.label ?? module}`);
    } catch {
      notify.error('Could not switch workspace');
    }
  };

  if (!canSwitchApps && variant !== 'sidebar') {
    return null;
  }

  if (!canSwitchApps && variant === 'sidebar') {
    return (
      <div
        className={cn(
          'workspace-sidebar-app-switcher workspace-sidebar-app-switcher--static',
          collapsed && 'workspace-sidebar-app-switcher--collapsed',
        )}
      >
        <Tooltip content={activeLabel}>
          <span className="workspace-sidebar-app-switcher-icon" aria-hidden>
            <ActiveIcon sx={{ fontSize: 20 }} />
          </span>
        </Tooltip>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="workspace-sidebar-app-switcher-kicker">Current app</p>
            <p className="workspace-sidebar-app-switcher-label truncate">{activeLabel}</p>
          </div>
        )}
      </div>
    );
  }

  const trigger =
    variant === 'sidebar' ? (
      collapsed ? (
        <Tooltip content={`Switch app · ${activeLabel}`}>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={cn(
              'workspace-sidebar-app-switcher workspace-sidebar-app-switcher--collapsed',
              open && 'workspace-sidebar-app-switcher--open',
              switching && 'pointer-events-none opacity-60',
            )}
            disabled={switching}
            aria-expanded={open}
            aria-haspopup="true"
            aria-label={`Switch app, current: ${activeLabel}`}
          >
            <ActiveIcon sx={{ fontSize: 22 }} />
          </button>
        </Tooltip>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'workspace-sidebar-app-switcher',
            open && 'workspace-sidebar-app-switcher--open',
            switching && 'pointer-events-none opacity-60',
          )}
          disabled={switching}
          aria-expanded={open}
          aria-haspopup="true"
        >
          <span className="workspace-sidebar-app-switcher-icon">
            <ActiveIcon sx={{ fontSize: 20 }} />
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="workspace-sidebar-app-switcher-kicker">Switch app</span>
            <span className="workspace-sidebar-app-switcher-label truncate">{activeLabel}</span>
          </span>
          <UnfoldMoreIcon
            sx={{ fontSize: 20 }}
            className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
          />
        </button>
      )
    ) : (
      <Tooltip content="Switch application">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'app-launcher-trigger',
            open && 'app-launcher-trigger--open',
            switching && 'pointer-events-none opacity-60',
          )}
          disabled={switching}
          aria-expanded={open}
          aria-haspopup="true"
        >
          <AppsIcon fontSize="small" />
          <span className="hidden sm:inline">Apps</span>
          <span className="hidden text-slate-400 sm:inline" aria-hidden>
            ▼
          </span>
        </button>
      </Tooltip>
    );

  const panel =
    open && panelCoords
      ? createPortal(
          <div
            data-app-launcher-panel="true"
            className={cn(
              'app-launcher-panel',
              variant === 'sidebar' && 'app-launcher-panel--sidebar',
            )}
            style={{
              top: panelCoords.top,
              bottom: panelCoords.bottom,
              left: panelCoords.left,
              width: panelCoords.width,
            }}
            role="menu"
          >
            <div className="app-launcher-panel-header">
              <p className="app-launcher-panel-title">
                {variant === 'sidebar' ? 'Your applications' : 'Your products'}
              </p>
              <p className="app-launcher-panel-subtitle">
                {variant === 'sidebar'
                  ? 'Pick a workspace to open'
                  : 'Only apps enabled for your organization'}
              </p>
            </div>
            {variant === 'sidebar' ? (
              <div className="app-launcher-list">
                {subscribedApps.map((entry) => {
                  const Icon = MODULE_ICONS[entry.erpModule] ?? HubIcon;
                  const isActive = entry.erpModule === activeModule;
                  return (
                    <button
                      key={entry.slug}
                      type="button"
                      role="menuitem"
                      onClick={() => handleSwitch(entry.erpModule)}
                      className={cn('app-launcher-row', isActive && 'app-launcher-row--active')}
                    >
                      <span className="app-launcher-row-icon">
                        <Icon sx={{ fontSize: 18 }} />
                      </span>
                      <span className="app-launcher-row-label">{entry.label}</span>
                      {isActive ? (
                        <CheckIcon className="app-launcher-row-check" sx={{ fontSize: 18 }} />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="app-launcher-grid">
                {subscribedApps.map((entry) => {
                  const Icon = MODULE_ICONS[entry.erpModule] ?? HubIcon;
                  const isActive = entry.erpModule === activeModule;
                  return (
                    <button
                      key={entry.slug}
                      type="button"
                      role="menuitem"
                      onClick={() => handleSwitch(entry.erpModule)}
                      className={cn('app-launcher-tile', isActive && 'app-launcher-tile--active')}
                    >
                      <span className="app-launcher-tile-icon bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        <Icon fontSize="small" />
                      </span>
                      <span className="app-launcher-tile-label">{entry.label}</span>
                      {isActive && (
                        <CheckIcon className="app-launcher-tile-check" sx={{ fontSize: 16 }} />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn(variant === 'sidebar' && 'w-full')}>
      {trigger}
      {panel}
    </div>
  );
}
